// geographycontent7.js
// Section Seven — Development
// Grade9Ready Geography content module
//
// Covers:
// 92 Contrasts in Development
// 93 Measuring Development
// 95 Obstacles to Development
// 97 World Trade
// 98 Aid
// 99 Development Projects
// 102 Revision Summary for Section Seven

export const geographyContent7 = {
  id: "geography-section-7",
  subject: "Geography",
  sectionNumber: 7,
  title: "Development",
  slug: "development",
  examBoardLink:
    "KS3 Geography: international development, economic activity, global inequality, trade, aid and human geography",
  intro:
    "This section explains why some countries are richer and more developed than others, how development can be measured, what prevents development, how world trade works, and how aid and development projects can improve quality of life. It focuses on the contrast between wealth, opportunity, health, education and living standards in different parts of the world.",
  keySkills: [
    "Use development terminology accurately.",
    "Compare countries using social and economic indicators.",
    "Explain why development is uneven.",
    "Interpret development data, maps and graphs.",
    "Assess the strengths and weaknesses of different development indicators.",
    "Explain how trade and aid can affect development.",
    "Evaluate development projects using social, economic and environmental criteria."
  ],

  topics: [
    {
      id: "geo7-1-contrasts-in-development",
      title: "Contrasts in Development",
      slug: "contrasts-in-development",
      pageRef: "Source booklet p.92",
      learningObjectives: [
        "Define development.",
        "Describe differences in development between countries.",
        "Explain why development is more than just wealth."
      ],
      keywords: [
        {
          term: "Development",
          definition:
            "The progress of a country or place in improving wealth, health, education, services, opportunities and quality of life."
        },
        {
          term: "Quality of life",
          definition:
            "How good a person's life is, including health, safety, education, income, environment and access to services."
        },
        {
          term: "Standard of living",
          definition:
            "The level of wealth, comfort and material goods available to people."
        },
        {
          term: "HIC",
          definition:
            "Higher Income Country: a country with a high average income and generally high levels of development."
        },
        {
          term: "LIC",
          definition:
            "Lower Income Country: a country with a low average income and often lower levels of development."
        },
        {
          term: "NEE",
          definition:
            "Newly Emerging Economy: a country experiencing rapid economic growth and industrialisation."
        },
        {
          term: "Inequality",
          definition:
            "Uneven distribution of wealth, services and opportunities between people or places."
        }
      ],
      tutorial: [
        {
          heading: "What development means",
          body:
            "Development is about how much a place has improved people's lives. It includes income, jobs, education, healthcare, housing, food, clean water, safety and political stability. A richer country is often more developed, but money alone does not show the full picture."
        },
        {
          heading: "Contrasts between countries",
          body:
            "Some countries have high incomes, long life expectancy, strong healthcare, high literacy and reliable infrastructure. Others may face poverty, poor access to clean water, lower life expectancy, limited schooling and weaker transport or power networks."
        },
        {
          heading: "Development can vary within countries",
          body:
            "A country may be wealthy overall but still have poor regions or groups with limited access to services. Similarly, a poorer country may have wealthy cities and fast-growing industries. Development is uneven at global, national and local scales."
        }
      ],
      workedExamples: [
        {
          question:
            "Explain why development is more than just a country's income.",
          answer:
            "Income shows how much money a country has on average, but it does not show how wealth is shared or whether people have good healthcare, education, housing, clean water and safety. A fuller view of development needs social and economic indicators."
        },
        {
          question:
            "Describe one contrast between an HIC and an LIC.",
          answer:
            "An HIC usually has better access to healthcare and longer life expectancy, while an LIC may have fewer doctors, poorer sanitation and lower life expectancy."
        }
      ],
      commonMisconceptions: [
        "Development is not the same as wealth. Wealth is one part of development.",
        "Not everyone in an HIC is wealthy, and not everyone in an LIC is poor.",
        "Countries do not fit perfectly into simple categories; development is complex and uneven."
      ],
      quickCheck: [
        {
          question: "Which statement best defines development?",
          options: [
            "The improvement of wealth, health, education and quality of life",
            "The total land area of a country",
            "The number of people living in a city",
            "The amount of rainfall a country receives"
          ],
          answer:
            "The improvement of wealth, health, education and quality of life",
          explanation:
            "Development includes economic and social improvements."
        },
        {
          question: "Which is a social indicator of development?",
          options: [
            "Life expectancy",
            "Total exports",
            "Gross income only",
            "Number of factories"
          ],
          answer: "Life expectancy",
          explanation:
            "Life expectancy is a social indicator because it links to health and living conditions."
        }
      ],
      examPractice: [
        {
          question:
            "Explain why development can be uneven between and within countries.",
          marks: 6,
          indicativeContent:
            "Development is uneven between countries because some have higher incomes, better healthcare, stronger education systems, more stable governments and better infrastructure. It is also uneven within countries because wealth and services may be concentrated in cities or richer regions, while rural or poorer areas may have less access to jobs, schools and healthcare."
        }
      ],
      grade9Move:
        "Use both global and internal inequality in your answer. Strong answers recognise that development varies between countries and within countries."
    },

    {
      id: "geo7-2-measuring-development",
      title: "Measuring Development",
      slug: "measuring-development",
      pageRef: "Source booklet p.93",
      learningObjectives: [
        "Identify different development indicators.",
        "Explain the strengths and weaknesses of development indicators.",
        "Use more than one indicator to compare countries."
      ],
      keywords: [
        {
          term: "Development indicator",
          definition:
            "A statistic used to measure one aspect of development."
        },
        {
          term: "GNI per capita",
          definition:
            "Gross National Income per person; the average income per person in a country."
        },
        {
          term: "GDP per capita",
          definition:
            "Gross Domestic Product per person; the average value of goods and services produced per person."
        },
        {
          term: "Life expectancy",
          definition:
            "The average number of years a person is expected to live."
        },
        {
          term: "Infant mortality rate",
          definition:
            "The number of babies who die before their first birthday per 1,000 live births."
        },
        {
          term: "Literacy rate",
          definition:
            "The percentage of people who can read and write."
        },
        {
          term: "HDI",
          definition:
            "Human Development Index; a combined measure using income, education and life expectancy."
        },
        {
          term: "Composite indicator",
          definition:
            "An indicator made from several different measures."
        }
      ],
      tutorial: [
        {
          heading: "Why development is measured",
          body:
            "Development indicators allow geographers to compare places. They help show whether people are living longer, earning more, attending school and accessing services. No single indicator gives the whole picture."
        },
        {
          heading: "Economic indicators",
          body:
            "GNI per capita and GDP per capita measure average income or economic output per person. They are useful for comparing wealth, but they can hide inequality because they are averages."
        },
        {
          heading: "Social indicators",
          body:
            "Life expectancy, infant mortality, literacy and access to clean water help show how people actually live. These indicators often reveal quality of life more clearly than income alone."
        },
        {
          heading: "HDI",
          body:
            "The Human Development Index combines income, education and life expectancy. Because it uses more than one indicator, it gives a broader picture of development than income alone."
        }
      ],
      workedExamples: [
        {
          question:
            "Why might GNI per capita be misleading?",
          answer:
            "GNI per capita is an average, so it can hide inequality. A country may have a high average income because a small number of people are very wealthy, while many others remain poor."
        },
        {
          question:
            "Why is HDI useful?",
          answer:
            "HDI is useful because it combines income, education and life expectancy, giving a broader view of development than a single economic indicator."
        }
      ],
      commonMisconceptions: [
        "GDP and GNI per capita do not show how wealth is shared.",
        "A country can have rising income but still have poor education or healthcare.",
        "HDI is useful, but it still cannot show every aspect of quality of life."
      ],
      quickCheck: [
        {
          question:
            "Which indicator measures the average number of years people are expected to live?",
          options: [
            "Life expectancy",
            "Literacy rate",
            "GNI per capita",
            "Infant mortality"
          ],
          answer: "Life expectancy",
          explanation:
            "Life expectancy measures average expected lifespan."
        },
        {
          question:
            "Why is HDI described as a composite indicator?",
          options: [
            "It combines several indicators",
            "It measures only rainfall",
            "It counts only total population",
            "It measures only trade"
          ],
          answer: "It combines several indicators",
          explanation:
            "HDI combines income, education and life expectancy."
        }
      ],
      examPractice: [
        {
          question:
            "Explain why using several development indicators is better than using one indicator.",
          marks: 6,
          indicativeContent:
            "Using one indicator can give a limited or misleading picture. For example, GNI per capita shows average income but hides inequality and does not show health or education. Several indicators, such as life expectancy, literacy rate, infant mortality and income, provide a broader picture of quality of life and development."
        },
        {
          question:
            "Assess the usefulness of HDI as a measure of development.",
          marks: 8,
          indicativeContent:
            "HDI is useful because it combines income, education and life expectancy, so it gives a broader picture than income alone. It allows countries to be compared easily. However, it is still an average and may hide inequality within a country. It also does not directly measure political freedom, environmental quality, safety or gender inequality. Overall, it is useful but should be supported by other indicators."
        }
      ],
      grade9Move:
        "When evaluating indicators, always mention averages and hidden inequality. This shows more sophisticated geographical thinking."
    },

    {
      id: "geo7-3-obstacles-to-development",
      title: "Obstacles to Development",
      slug: "obstacles-to-development",
      pageRef: "Source booklet p.95",
      learningObjectives: [
        "Identify physical, economic, historical and political obstacles to development.",
        "Explain how obstacles can slow development.",
        "Understand how obstacles can link together in a cycle."
      ],
      keywords: [
        {
          term: "Obstacle to development",
          definition:
            "A factor that slows or prevents improvements in quality of life and economic progress."
        },
        {
          term: "Landlocked",
          definition:
            "A country with no coastline, making sea trade more difficult."
        },
        {
          term: "Debt",
          definition:
            "Money owed by a country, organisation or person."
        },
        {
          term: "Conflict",
          definition:
            "War or violence that can damage lives, infrastructure and economic activity."
        },
        {
          term: "Corruption",
          definition:
            "Dishonest or illegal use of power, often involving money or favouritism."
        },
        {
          term: "Colonialism",
          definition:
            "Control of one country or region by another, often involving exploitation of resources and people."
        },
        {
          term: "Natural hazard",
          definition:
            "A natural event such as drought, earthquake, flood or tropical storm that threatens people and property."
        },
        {
          term: "Cycle of poverty",
          definition:
            "A situation where poverty causes conditions that make it difficult for people or countries to escape poverty."
        }
      ],
      tutorial: [
        {
          heading: "Physical obstacles",
          body:
            "Physical obstacles include drought, floods, tropical storms, disease, poor soils, mountainous relief, extreme climate and being landlocked. These can make farming, transport, trade and settlement more difficult."
        },
        {
          heading: "Economic obstacles",
          body:
            "Countries may struggle to develop if they rely on low-value exports, have high debt, lack investment, or have poor infrastructure. If a country spends money repaying debt, it may have less to spend on schools, healthcare and transport."
        },
        {
          heading: "Historical and political obstacles",
          body:
            "Colonialism, conflict, weak governance and corruption can slow development. Conflict damages homes, roads, schools and hospitals. Corruption can mean money is not spent on services that benefit the population."
        },
        {
          heading: "Linked obstacles",
          body:
            "Obstacles often reinforce each other. For example, drought can reduce crop yields, lowering income. Lower income means less tax for the government, reducing spending on infrastructure, which makes future development harder."
        }
      ],
      workedExamples: [
        {
          question:
            "Explain how being landlocked can slow development.",
          answer:
            "Landlocked countries have no coastline, so importing and exporting goods by sea is harder. They may depend on neighbouring countries' roads, railways and ports, increasing transport costs and reducing trade opportunities."
        },
        {
          question:
            "Explain how conflict can become an obstacle to development.",
          answer:
            "Conflict can destroy infrastructure, close schools, damage hospitals, force people to migrate and discourage investment. This reduces economic activity and lowers quality of life."
        }
      ],
      commonMisconceptions: [
        "Poverty is rarely caused by one factor alone. Obstacles often work together.",
        "Physical geography can influence development, but it does not completely determine a country's future.",
        "Aid alone may not solve development problems if conflict, corruption or poor infrastructure remain."
      ],
      quickCheck: [
        {
          question:
            "Which is a physical obstacle to development?",
          options: [
            "Drought",
            "Debt repayments",
            "Corruption",
            "Poor trade agreements"
          ],
          answer: "Drought",
          explanation:
            "Drought is a physical factor because it is linked to climate and water supply."
        },
        {
          question:
            "How can debt slow development?",
          options: [
            "Money used for repayments cannot be spent on services",
            "It always increases school funding",
            "It removes all poverty immediately",
            "It guarantees higher trade income"
          ],
          answer:
            "Money used for repayments cannot be spent on services",
          explanation:
            "Debt repayments can reduce money available for development spending."
        }
      ],
      examPractice: [
        {
          question:
            "Explain two obstacles that can slow a country's development.",
          marks: 4,
          indicativeContent:
            "Conflict can slow development because it damages infrastructure, disrupts education and healthcare, and discourages investment. Debt can also slow development because money spent on repayments cannot be used to improve schools, hospitals, roads or water supplies."
        },
        {
          question:
            "Explain how obstacles to development can link together.",
          marks: 6,
          indicativeContent:
            "Obstacles can create a cycle. For example, drought reduces crop yields, lowering farmers' income. This reduces tax revenue and may increase food shortages. The government may have less money to invest in irrigation, roads or education, making the country more vulnerable to future drought and poverty."
        }
      ],
      grade9Move:
        "Use chain reasoning: obstacle → immediate effect → wider economic/social impact → how this slows future development."
    },

    {
      id: "geo7-4-world-trade",
      title: "World Trade",
      slug: "world-trade",
      pageRef: "Source booklet p.97",
      learningObjectives: [
        "Explain what world trade is.",
        "Describe how trade can help development.",
        "Explain why trade can sometimes increase inequality."
      ],
      keywords: [
        {
          term: "Trade",
          definition:
            "The buying and selling of goods and services."
        },
        {
          term: "Export",
          definition:
            "A good or service sold to another country."
        },
        {
          term: "Import",
          definition:
            "A good or service bought from another country."
        },
        {
          term: "Primary product",
          definition:
            "A raw material such as coffee, cotton, oil, copper or cocoa."
        },
        {
          term: "Manufactured good",
          definition:
            "A product made from raw materials, often worth more than the original materials."
        },
        {
          term: "Terms of trade",
          definition:
            "The relationship between the price a country receives for exports and the price it pays for imports."
        },
        {
          term: "Fair trade",
          definition:
            "Trade that aims to give producers a fairer price and better working conditions."
        },
        {
          term: "Globalisation",
          definition:
            "The increasing connections between countries through trade, investment, technology, migration and culture."
        }
      ],
      tutorial: [
        {
          heading: "How world trade works",
          body:
            "Countries buy and sell goods and services. Exports bring money into a country, while imports are goods or services bought from elsewhere. Trade can create jobs, increase income and help countries develop."
        },
        {
          heading: "Primary products and manufactured goods",
          body:
            "Many poorer countries export primary products such as cocoa, coffee or minerals. These often have lower and more unstable prices than manufactured goods. Richer countries often sell manufactured products or high-value services for more money."
        },
        {
          heading: "Unequal trade",
          body:
            "Trade can increase inequality if poorer countries receive low prices for raw materials but pay high prices for manufactured goods. This makes it harder to earn enough money to invest in development."
        },
        {
          heading: "Fair trade",
          body:
            "Fair trade aims to give producers a better price, safer working conditions and community investment. It can help farmers earn a more reliable income, although it does not solve every trade issue."
        }
      ],
      workedExamples: [
        {
          question:
            "Explain why exporting raw materials may limit development.",
          answer:
            "Raw materials often have lower and more unstable prices than manufactured goods. If a country relies on these exports, its income may be low or unpredictable, making it harder to invest in schools, healthcare and infrastructure."
        },
        {
          question:
            "How can trade support development?",
          answer:
            "Trade can create jobs, bring income into a country, attract investment and provide tax revenue that governments can spend on services and infrastructure."
        }
      ],
      commonMisconceptions: [
        "Trade is not automatically fair or equally beneficial.",
        "Exporting more does not always mean a country becomes rich; the value of exports matters.",
        "Fair trade helps some producers, but it is not a complete solution to global inequality."
      ],
      quickCheck: [
        {
          question:
            "What is an export?",
          options: [
            "A good or service sold to another country",
            "A good bought from another country",
            "A country's birth rate",
            "A measure of infant mortality"
          ],
          answer: "A good or service sold to another country",
          explanation:
            "Exports are sold overseas and bring money into a country."
        },
        {
          question:
            "Which is a primary product?",
          options: [
            "Cocoa beans",
            "A laptop",
            "A car",
            "A mobile phone app"
          ],
          answer: "Cocoa beans",
          explanation:
            "Cocoa beans are a raw material, so they are a primary product."
        }
      ],
      examPractice: [
        {
          question:
            "Explain how world trade can both help and hinder development.",
          marks: 8,
          indicativeContent:
            "World trade can help development by creating jobs, increasing income, attracting investment and raising tax revenue for services and infrastructure. However, trade can hinder development if poorer countries rely on low-value primary products with unstable prices, while paying high prices for manufactured goods. Unequal trade relationships can make it difficult for LICs to invest in long-term development."
        }
      ],
      grade9Move:
        "In trade answers, compare value added: raw materials usually earn less, while manufactured goods and services usually earn more."
    },

    {
      id: "geo7-5-aid",
      title: "Aid",
      slug: "aid",
      pageRef: "Source booklet p.98",
      learningObjectives: [
        "Define aid.",
        "Identify different types of aid.",
        "Evaluate the advantages and disadvantages of aid."
      ],
      keywords: [
        {
          term: "Aid",
          definition:
            "Help given by one country, organisation or group to another, often as money, food, equipment, expertise or emergency support."
        },
        {
          term: "Emergency aid",
          definition:
            "Short-term aid given after disasters such as earthquakes, floods, droughts or conflict."
        },
        {
          term: "Development aid",
          definition:
            "Long-term aid designed to improve quality of life and support development."
        },
        {
          term: "Bilateral aid",
          definition:
            "Aid given directly from one country to another."
        },
        {
          term: "Multilateral aid",
          definition:
            "Aid given through international organisations such as the United Nations or World Bank."
        },
        {
          term: "NGO",
          definition:
            "Non-governmental organisation, such as a charity, that works independently from government."
        },
        {
          term: "Tied aid",
          definition:
            "Aid given with conditions attached, such as requiring the receiving country to buy goods from the donor country."
        },
        {
          term: "Appropriate technology",
          definition:
            "Technology suited to local skills, needs, cost and environment."
        }
      ],
      tutorial: [
        {
          heading: "What aid is",
          body:
            "Aid is support given to countries, regions or communities. It can be money, food, clean water, tents, medicines, school equipment, training, technology or expert help."
        },
        {
          heading: "Emergency and long-term aid",
          body:
            "Emergency aid responds quickly to disasters by providing food, shelter, water and medical care. Long-term development aid aims to improve education, healthcare, farming, water supply and infrastructure so places become more self-reliant."
        },
        {
          heading: "Advantages of aid",
          body:
            "Aid can save lives, reduce suffering, rebuild infrastructure, improve education and healthcare, and help communities recover from disasters. Small-scale aid using appropriate technology can be especially effective."
        },
        {
          heading: "Problems with aid",
          body:
            "Aid can create dependency if countries rely on outside support. It may be lost through corruption, fail to reach those most in need, or come with conditions that benefit the donor country."
        }
      ],
      workedExamples: [
        {
          question:
            "Explain one difference between emergency aid and development aid.",
          answer:
            "Emergency aid is short-term support after a crisis, such as food, water and shelter after an earthquake. Development aid is longer-term support, such as building schools, clinics or water systems."
        },
        {
          question:
            "Why might tied aid be criticised?",
          answer:
            "Tied aid can be criticised because it may benefit the donor country as much as the recipient, for example by requiring the recipient to buy goods or services from the donor."
        }
      ],
      commonMisconceptions: [
        "Aid is not always money; it can include equipment, training, food, water and expertise.",
        "Aid is not always successful. Its impact depends on how it is managed and who receives it.",
        "Emergency aid and development aid have different timescales and purposes."
      ],
      quickCheck: [
        {
          question:
            "Which type of aid is most likely immediately after an earthquake?",
          options: [
            "Emergency aid",
            "Tied aid only",
            "No aid",
            "Long-term trade only"
          ],
          answer: "Emergency aid",
          explanation:
            "Emergency aid provides urgent support after disasters."
        },
        {
          question:
            "What is bilateral aid?",
          options: [
            "Aid from one country directly to another",
            "Aid through several international organisations only",
            "Trade between two businesses",
            "A measure of life expectancy"
          ],
          answer: "Aid from one country directly to another",
          explanation:
            "Bilateral aid is direct country-to-country aid."
        }
      ],
      examPractice: [
        {
          question:
            "Evaluate the use of aid as a way to support development.",
          marks: 8,
          indicativeContent:
            "Aid can support development by saving lives after disasters, improving healthcare, providing clean water, building schools and funding infrastructure. Long-term aid can improve skills and self-reliance. However, aid may create dependency, be affected by corruption, fail to reach those most in need, or be tied to conditions that benefit the donor. Overall, aid is most effective when it is targeted, transparent, locally appropriate and supports long-term independence."
        }
      ],
      grade9Move:
        "Separate short-term humanitarian value from long-term development impact. This creates a more balanced evaluation."
    },

    {
      id: "geo7-6-development-projects",
      title: "Development Projects",
      slug: "development-projects",
      pageRef: "Source booklet p.99",
      learningObjectives: [
        "Describe different types of development project.",
        "Explain how development projects can improve quality of life.",
        "Evaluate whether development projects are sustainable."
      ],
      keywords: [
        {
          term: "Development project",
          definition:
            "A planned scheme designed to improve living standards, services, income or infrastructure."
        },
        {
          term: "Infrastructure",
          definition:
            "Basic systems and services such as roads, water, electricity, schools, hospitals and communications."
        },
        {
          term: "Sustainable development",
          definition:
            "Development that meets present needs without damaging the ability of future generations to meet their needs."
        },
        {
          term: "Bottom-up project",
          definition:
            "A small-scale project led by or designed with local communities."
        },
        {
          term: "Top-down project",
          definition:
            "A large-scale project planned by governments or major organisations."
        },
        {
          term: "Appropriate technology",
          definition:
            "Technology that is affordable, repairable and suitable for local people and conditions."
        },
        {
          term: "Microfinance",
          definition:
            "Small loans given to people or groups to help them start or expand small businesses."
        },
        {
          term: "Sustainability",
          definition:
            "The ability of something to continue long term without causing unacceptable social, economic or environmental harm."
        }
      ],
      tutorial: [
        {
          heading: "Types of development project",
          body:
            "Development projects can be small-scale, such as wells, water pumps, school buildings, crop training or microfinance. They can also be large-scale, such as dams, roads, ports, power stations or irrigation schemes."
        },
        {
          heading: "Bottom-up development",
          body:
            "Bottom-up projects usually involve local people and focus on local needs. They are often cheaper, easier to maintain and more appropriate to community conditions."
        },
        {
          heading: "Top-down development",
          body:
            "Top-down projects can improve infrastructure and support economic growth on a large scale. However, they can be expensive, may displace people, damage environments or fail to meet local needs."
        },
        {
          heading: "Evaluating development projects",
          body:
            "A strong evaluation considers social benefits, economic costs and environmental impacts. A project is more likely to be sustainable if local people can maintain it, afford it and benefit from it long term."
        }
      ],
      workedExamples: [
        {
          question:
            "Explain why a village water pump can support development.",
          answer:
            "A water pump can provide safer drinking water close to homes. This reduces disease and saves time, especially for children who may then attend school more regularly. Health and education improvements support development."
        },
        {
          question:
            "Explain one possible disadvantage of a large dam project.",
          answer:
            "A large dam may flood farmland or villages, forcing people to move. It may also disrupt river ecosystems even if it provides electricity and water storage."
        }
      ],
      commonMisconceptions: [
        "Large projects are not always better than small projects.",
        "A project can bring economic benefits while still causing social or environmental costs.",
        "Development projects should be judged by long-term sustainability, not just immediate impact."
      ],
      quickCheck: [
        {
          question:
            "Which is most likely to be a bottom-up development project?",
          options: [
            "A locally managed water pump",
            "A huge international airport",
            "A national motorway network",
            "A major hydroelectric dam"
          ],
          answer: "A locally managed water pump",
          explanation:
            "Bottom-up projects are small-scale and community-focused."
        },
        {
          question:
            "What does sustainable development aim to do?",
          options: [
            "Meet present needs without harming future generations",
            "Use all resources as quickly as possible",
            "Focus only on profit",
            "Stop all economic activity"
          ],
          answer:
            "Meet present needs without harming future generations",
          explanation:
            "Sustainable development balances present improvement with future protection."
        }
      ],
      examPractice: [
        {
          question:
            "Compare top-down and bottom-up development projects.",
          marks: 6,
          indicativeContent:
            "Top-down projects are large-scale schemes planned by governments or major organisations, such as dams or roads. They can improve infrastructure and support economic growth but may be expensive and cause environmental or social damage. Bottom-up projects are smaller and community-focused, such as wells or microfinance. They are often cheaper and more appropriate, but may have a smaller overall impact."
        },
        {
          question:
            "Evaluate the importance of sustainability in development projects.",
          marks: 8,
          indicativeContent:
            "Sustainability is important because projects should improve quality of life without creating future problems. Sustainable projects are affordable, locally appropriate, maintainable and environmentally responsible. For example, a water pump that local people can repair may have long-term benefits. However, some large projects may bring rapid economic growth even if they have environmental costs. Overall, sustainable projects are more likely to create lasting development."
        }
      ],
      grade9Move:
        "Use the three-way evaluation: social, economic and environmental. Then make a judgement about long-term sustainability."
    }
  ],

  sectionReview: {
    title: "Section Seven Review — Development",
    summary:
      "Development is about improving quality of life, not just increasing wealth. It can be measured using economic and social indicators, but each indicator has limitations. Development is uneven because countries face different physical, economic, historical and political obstacles. Trade can support development, but unfair trade patterns can increase inequality. Aid and development projects can improve lives, especially when they are appropriate, transparent and sustainable.",
    mustKnow: [
      "Development means improving wealth, health, education, services, opportunities and quality of life.",
      "Development is uneven between countries and within countries.",
      "GNI or GDP per capita measure average income, but they can hide inequality.",
      "Life expectancy, literacy rate and infant mortality are social indicators of development.",
      "HDI combines income, education and life expectancy.",
      "Obstacles to development include conflict, debt, corruption, natural hazards, disease, landlocked location and colonial history.",
      "World trade can help countries earn income, but trade can be unequal.",
      "Primary products are often lower value than manufactured goods.",
      "Aid can be emergency, long-term, bilateral, multilateral, voluntary or tied.",
      "Aid can save lives and improve services, but may create dependency or be affected by corruption.",
      "Top-down projects are large-scale and government-led.",
      "Bottom-up projects are smaller, local and often more appropriate.",
      "Sustainable development balances social, economic and environmental needs."
    ],
    highValueExamPhrases: [
      "Development is a broad concept that includes both economic and social progress...",
      "This indicator is useful, but it can hide inequality because it is an average...",
      "A single indicator does not show the full picture of quality of life...",
      "These obstacles can reinforce each other, creating a cycle of poverty...",
      "Trade can increase income, but the value of exports matters...",
      "Reliance on low-value primary products can limit development...",
      "Aid is most effective when it is targeted, transparent and locally appropriate...",
      "Bottom-up projects are often more sustainable because they involve local people...",
      "The success of the project depends on social, economic and environmental impacts..."
    ],
    mixedPractice: [
      {
        question:
          "Explain why development is difficult to measure accurately.",
        marks: 6,
        indicativeContent:
          "Development is difficult to measure because it includes many aspects of life, such as income, health, education, safety and access to services. Single indicators are limited. For example, GNI per capita shows average income but hides inequality. Social indicators such as life expectancy or literacy show quality of life, but still do not show everything. A range of indicators gives a more accurate picture."
      },
      {
        question:
          "Explain two obstacles that can prevent development.",
        marks: 4,
        indicativeContent:
          "Conflict can prevent development by destroying infrastructure, closing schools and hospitals, and discouraging investment. Debt can also slow development because money used for repayments cannot be spent on healthcare, education, roads or clean water."
      },
      {
        question:
          "Explain how world trade can affect development.",
        marks: 6,
        indicativeContent:
          "World trade can support development by creating jobs, increasing income and raising tax revenue. However, countries that rely on primary products may receive low or unstable prices, while paying more for manufactured goods. This can make it harder to invest in services and infrastructure, limiting development."
      },
      {
        question:
          "Evaluate the use of aid in reducing the development gap.",
        marks: 8,
        indicativeContent:
          "Aid can reduce the development gap by providing emergency support, clean water, healthcare, education, food and infrastructure. Long-term aid can improve skills and self-reliance. However, aid may not reach the poorest people if corruption occurs, and tied aid may benefit donor countries. Aid can also create dependency if it does not build local capacity. Overall, aid is most effective when it is long-term, transparent and locally appropriate."
      },
      {
        question:
          "Assess whether bottom-up development projects are better than top-down projects.",
        marks: 8,
        indicativeContent:
          "Bottom-up projects are often more appropriate because they involve local people, use local skills and are cheaper to maintain. They can directly improve quality of life through water, health, education or microfinance. However, their impact may be small-scale. Top-down projects can transform infrastructure and create national economic growth, but may be expensive and cause social or environmental damage. The best approach depends on the problem, scale and sustainability of the project."
      }
    ]
  }
};

export default geographyContent7;